import {
  canAddEquationNodes,
  createMatrixNode,
  createVectorNode,
} from "./equation-operations";
import { draftMatrixValues, draftVectorValues } from "./equation-values";
import { matrix as toMatrix, vector as toVector } from "@src/math/matrix";
import {
  moveItemTo as moveCollectionItemTo,
  moveItemBy,
  removeItem,
} from "./item-ordering";
import type { Equation } from "./equation";
import type { Dimension, MatrixFor } from "@src/math/types";
import type { MoveDirection } from "./item-ordering";
import { analyzeEquationCapabilities } from "./capability-analysis";
import { getMatrixPresets } from "./presets";

const VECTOR_COLORS = [
  "#f4b740",
  "#5bd8a6",
  "#ef6f6c",
  "#8fb4ff",
  "#d989ff",
  "#5ed5e8",
];

function nextMatrixLabel(existingLabels: string[]): string {
  const labels = new Set(existingLabels);
  for (let index = 0; ; index += 1) {
    const label = alphabeticLabel(index);
    if (!labels.has(label)) return label;
  }
}

function nextVectorLabel(existingLabels: string[]): string {
  const labels = new Set(existingLabels);
  for (let index = 1; ; index += 1) {
    const label = `v${String(index)}`;
    if (!labels.has(label)) return label;
  }
}

export function alphabeticLabel(index: number): string {
  if (!Number.isInteger(index) || index < 0)
    throw new Error("Invalid label index");
  let value = index + 1;
  let label = "";
  while (value > 0) {
    value -= 1;
    label = String.fromCharCode(65 + (value % 26)) + label;
    value = Math.floor(value / 26);
  }
  return label;
}

export function addMatrix<D extends Dimension>(
  equation: Equation<D>,
  createId: () => string,
): void {
  assertCanAdd(equation, "matrices");
  equation.matrices.unshift(createMatrix(equation, createId));
}

export function addVector<D extends Dimension>(
  equation: Equation<D>,
  createId: () => string,
): void {
  assertCanAdd(equation, "vectors");
  equation.vectors.push(createVector(equation, createId));
}

export function addMatrixPreset<D extends Dimension>(
  equation: Equation<D>,
  presetId: string,
  createId: () => string,
): void {
  assertCanAdd(equation, "matrices");
  const preset = getMatrixPresets(equation.dimension).find(
    (preset) => preset.id === presetId,
  );
  if (!preset) throw new Error(`Unknown matrix preset: ${presetId}`);
  equation.matrices.unshift(
    createMatrix(equation, createId, preset.values, preset.draftValues),
  );
}

export function addInverseTransform<D extends Dimension>(
  equation: Equation<D>,
  createId: () => string,
): void {
  assertCanAdd(equation, "matrices");
  const inverse = analyzeEquationCapabilities(equation).inverseTransform;
  if (inverse.kind !== "available")
    throw new Error("Inverse transform is unavailable");
  equation.matrices.unshift(
    createMatrix(
      equation,
      createId,
      toMatrix(equation.dimension, inverse.value),
      inverse.value.map(formatInputNumber),
    ),
  );
}

export function addEigenbasis<D extends Dimension>(
  equation: Equation<D>,
  createId: () => string,
): void {
  const capabilities = analyzeEquationCapabilities(equation);
  const eigenbasis = capabilities.eigenbasis;
  if (eigenbasis.kind !== "available")
    throw new Error("Eigenbasis is unavailable");
  const serializedVectors = eigenbasis.value.map((components) =>
    components.map(formatInputNumber),
  );
  const vectors = [...equation.vectors];
  for (const [index, components] of eigenbasis.value.entries()) {
    vectors.push(
      createVector(
        equation,
        createId,
        components,
        serializedVectors[index],
        vectors,
      ),
    );
  }
  equation.vectors = vectors;
}

export function addRepresentativeEigenvector<D extends Dimension>(
  equation: Equation<D>,
  createId: () => string,
): void {
  const capabilities = analyzeEquationCapabilities(equation);
  const eigenvector = capabilities.eigenvector;
  if (eigenvector.kind !== "available")
    throw new Error("Eigenvector is unavailable");
  equation.vectors.push(
    createVector(
      equation,
      createId,
      eigenvector.value,
      eigenvector.value.map(formatInputNumber),
    ),
  );
}

export function setMatrixEntry<D extends Dimension>(
  equation: Equation<D>,
  id: string,
  index: number,
  value: string,
): boolean {
  const matrix = equation.matrices.find((matrix) => matrix.id === id);
  if (!matrix) throw new Error(`Missing matrix: ${id}`);
  if (matrix.entries[index] === value) return false;
  const entries = [...matrix.entries];
  entries[index] = value;
  equation.matrices = equation.matrices.map((candidate) =>
    candidate.id === id
      ? {
          ...candidate,
          entries: draftMatrixValues(equation.dimension, entries),
        }
      : candidate,
  );
  return true;
}

export function setMatrixDuration<D extends Dimension>(
  equation: Equation<D>,
  id: string,
  durationMs: number,
): boolean {
  const matrix = equation.matrices.find((matrix) => matrix.id === id);
  if (!matrix) throw new Error(`Missing matrix: ${id}`);
  if (matrix.durationMs === durationMs) return false;
  equation.matrices = equation.matrices.map((candidate) =>
    candidate.id === id ? { ...candidate, durationMs } : candidate,
  );
  return true;
}

export function setVectorCoordinate<D extends Dimension>(
  equation: Equation<D>,
  id: string,
  index: number,
  value: string,
): boolean {
  const vector = equation.vectors.find((vector) => vector.id === id);
  if (!vector) throw new Error(`Missing vector: ${id}`);
  if (vector.coordinates[index] === value) return false;
  const coordinates = [...vector.coordinates];
  coordinates[index] = value;
  equation.vectors = equation.vectors.map((candidate) =>
    candidate.id === id
      ? {
          ...candidate,
          coordinates: draftVectorValues(equation.dimension, coordinates),
        }
      : candidate,
  );
  return true;
}

export function deleteEquationItem<D extends Dimension>(
  equation: Equation<D>,
  kind: "matrix" | "vector",
  id: string,
): void {
  if (kind === "matrix") removeItem(equation.matrices, id);
  else removeItem(equation.vectors, id);
}

export function moveEquationItem<D extends Dimension>(
  equation: Equation<D>,
  kind: "matrix" | "vector",
  id: string,
  direction: MoveDirection,
): number | null {
  const result =
    kind === "matrix"
      ? moveItemBy(equation.matrices, id, direction)
      : moveItemBy(equation.vectors, id, direction);
  return result;
}

export function moveEquationItemTo<D extends Dimension>(
  equation: Equation<D>,
  kind: "matrix" | "vector",
  id: string,
  targetId: string,
  side: "before" | "after",
): number | null {
  const result =
    kind === "matrix"
      ? moveCollectionItemTo(equation.matrices, id, targetId, side)
      : moveCollectionItemTo(equation.vectors, id, targetId, side);
  return result;
}

function createMatrix<D extends Dimension>(
  equation: Equation<D>,
  createId: () => string,
  values?: MatrixFor<D>,
  draftValues?: readonly string[],
) {
  const id = createId();
  assertUniqueNodeId(equation, id);
  return createMatrixNode(
    equation.dimension,
    nextMatrixLabel(equation.matrices.map(({ label }) => label)),
    values,
    draftValues,
    id,
  );
}

function createVector<D extends Dimension>(
  equation: Equation<D>,
  createId: () => string,
  components?: readonly number[],
  draftComponents?: readonly string[],
  vectors = equation.vectors,
) {
  const label = nextVectorLabel(vectors.map((vector) => vector.label));
  const id = createId();
  assertUniqueNodeId(equation, id, vectors);
  return createVectorNode(
    equation.dimension,
    label,
    vectorColorForLabel(label),
    components ? toVector(equation.dimension, components) : undefined,
    draftComponents,
    id,
  );
}

function assertCanAdd<D extends Dimension>(
  equation: Equation<D>,
  kind: "matrices" | "vectors",
): void {
  if (!canAddEquationNodes(equation, kind))
    throw new Error(`Equation ${kind} capacity exceeded`);
}

function assertUniqueNodeId<D extends Dimension>(
  equation: Equation<D>,
  id: string,
  vectors = equation.vectors,
): void {
  if (
    equation.matrices.some((node) => node.id === id) ||
    vectors.some((node) => node.id === id)
  )
    throw new Error(`Duplicate generated node ID: ${id}`);
}

function vectorColorForLabel(label: string): string {
  const index = Number(label.slice(1)) - 1;
  return VECTOR_COLORS[index % VECTOR_COLORS.length];
}

function formatInputNumber(value: number): string {
  // Number#toString emits the shortest decimal that round-trips to the exact
  // same IEEE-754 value. In particular, do not erase tiny components: they
  // can be the only thing making two eigenvectors independent.
  return String(value);
}
